import { useEffect, useState } from 'react';
import { generatePath, useNavigate, useSearchParams } from 'react-router-dom';
import AppRoutes from 'constants/AppRoutes';
import { SEARCH_PARAMS } from 'constants/dataSets';
import { SHOW_ARCHIVED } from 'constants/formFields';
import { USER_PILLAR_MODULES } from 'constants/interfaces';
import useDataSetListActions from 'hooks/useDataSetListActions';
import useDownloadExample from 'hooks/useDownloadExample';
import useImportErrorModal from 'hooks/useImportErrorModal';
import usePillarModulePermission from 'hooks/usePillarModulePermission';
import {
  useCustomerArchiveMutation,
  useCustomerDeArchiveMutation,
  useCustomerImportRecordsMutation,
  useCustomerListQuery,
  useCustomerRecordDeleteMutation,
  useGetNaceCodeTreeQuery,
  useLazyCustomerExampleQuery,
  useProfitCenterListQuery,
} from 'store/slices/dataSets/apis/dataSetsApi';
import { useGetLocationsCountriesQuery } from 'store/slices/geoLocations/apis/geoLocationsApi';

import useDataSetsListForm from '../ProfitCentres/hooks/useDataSetsListForm';
import CustomersView from './CustomersView';

import type ICustomerItem from 'store/slices/dataSets/interfaces/ICustomerItem';
import type IArchiveError from '../ProfitCentres/interfaces/IArchiveError';
import type { TUseCustomerImportRecordsMutation } from './interfaces/TUseCustomerImportRecordsMutation';

function Customers() {
  const navigate = useNavigate();
  const {
    isActionsVisible,
    isLoading: isPillarModuleLoading,
  } = usePillarModulePermission(
    { pillarModuleKey: USER_PILLAR_MODULES.settings.dataSetsManagement },
  );

  const [getCustomersheetExample, { isLoading: isLazyCustomerExampleLoading }] = useLazyCustomerExampleQuery();
  const {
    control, getValues, setValue, watch,
  } = useDataSetsListForm();
  const [orderBy, setOrderBy] = useState('');
  const [searchParams] = useSearchParams();
  const [hasTriedArchived, setHasTriedArchived] = useState(false);

  const isArchived = watch(SHOW_ARCHIVED);
  const searchParam = searchParams.get(SEARCH_PARAMS.search) || '';
  const offset = Number(searchParams.get(SEARCH_PARAMS.offset) || 0);

  useEffect(() => {
    const isArchivedParam = searchParams.get(SEARCH_PARAMS.isArchived);
    const isArchivedFromUrl = isArchivedParam !== null ? Boolean(isArchivedParam === 'true') : false;

    if (isArchivedFromUrl !== isArchived) {
      setValue(SHOW_ARCHIVED, isArchivedFromUrl);
    }
  }, [searchParams, isArchived, setValue]);

  const { data, isFetching: isCustomerListLoading } = useCustomerListQuery({
    orderBy, keyword: searchParam, archived: isArchived, offset,
  });

  const [importRecords, { isLoading: isCustomerImportRecords }] = useCustomerImportRecordsMutation();

  const {
    isImportErrorModalVisible,
    importErrorMessage,
    handleImportError,
    handleCloseImportErrorModal,
  } = useImportErrorModal();

  const {
    uploadControl, isMappingModalVisible, onCloseMappingModal, isImportErrorsModalVisible,
    setIsImportErrorsModalVisible,
  } = useDownloadExample<TUseCustomerImportRecordsMutation>({
    importRecords,
    onError: handleImportError,
  });

  const { items } = data?.data || {};

  const [deArchive, { isLoading: isCustomerDeArchiveRecords, error: deArchiveError }] = useCustomerDeArchiveMutation();

  const [archive, { isLoading: isCustomerArchiveRecords, error: archiveError }] = useCustomerArchiveMutation();

  const [deleteRecord, {
    isLoading: isCustomerDeleteLoading,
    error: deleteError,
  }] = useCustomerRecordDeleteMutation();

  const { data: naceCodeTreeData = { data: {} }, isLoading: isGetNaceCodeTreeRecords } = useGetNaceCodeTreeQuery();

  const { data: locationsCountries, isFetching: isLocationsCountriesLoading } = useGetLocationsCountriesQuery();

  const {
    isArchiveModalVisible,
    isArchiveModalInfoVisible,
    latestArchivation,
    isLineMark,
    onSortChange,
    onSearchChange,
    onSelectAll,
    onArchiveConfirm,
    onArchive,
    onArchiveModalInfoOpen,
    onMoveToArchive,
    onDeArchive,
    onPageChange,
    isArchiveSelectedItemsVisible,
    setIsArchiveModalVisible,
    setIsArchiveModalInfoVisible,
    selectedItems,
    onShowArchiveChange,
    onOpenRestrictedModal,
    activeRestrictedModal,
    onDeleteRecord,
    isDeleteModalVisible,
    onCloseDeleteModal,
    onDeleteConfirm,
  } = useDataSetListActions<ICustomerItem[]>({
    items,
    archiveAction: archive,
    deArchiveAction: deArchive,
    deleteAction: deleteRecord,
    setOrderBy,
    getValues,
    setValue,
    watch,
  });

  const { data: profitCenterData, isLoading: isProfitCenterListRecords } = useProfitCenterListQuery({});

  const isWithSearch = !!searchParam;

  const isProfitCenterEmpty = !profitCenterData?.data?.meta?.count && isActionsVisible;

  const isUploadFileVisible = isActionsVisible && !isProfitCenterEmpty;

  const isEmptyList = !data?.data?.meta?.count || data?.data?.items?.length === 0;

  const currentTotalItems = items?.length || 0;

  const onDownloadExample = () => {
    getCustomersheetExample();
  };

  const onEditRecord = (recordId: number) => {
    navigate(generatePath(AppRoutes.customerEdit, { recordId: String(recordId) }));
  };

  const onAddRecord = () => {
    navigate(AppRoutes.customerCreate);
  };

  const meta = data?.data?.meta;

  const isLoading = isCustomerListLoading || isCustomerImportRecords || isCustomerDeArchiveRecords
      || isCustomerArchiveRecords || isCustomerDeleteLoading || isLazyCustomerExampleLoading || isGetNaceCodeTreeRecords
      || isProfitCenterListRecords || isPillarModuleLoading || isLocationsCountriesLoading;

  useEffect(() => {
    const isArchivedParam = searchParams.get(SEARCH_PARAMS.isArchived);
    if (isEmptyList && !isLoading && !isArchivedParam && !hasTriedArchived) {
      setHasTriedArchived(true);
      onShowArchiveChange(true);
    }
  }, [isEmptyList, isLoading, searchParams, onShowArchiveChange, hasTriedArchived]);

  return (
    <CustomersView
      isEmptyList={isEmptyList}
      uploadControl={uploadControl}
      isUploadFileVisible={isUploadFileVisible}
      isMappingModalVisible={isMappingModalVisible}
      setIsImportErrorsModalVisible={setIsImportErrorsModalVisible}
      onCloseMappingModal={onCloseMappingModal}
      isImportErrorsModalVisible={isImportErrorsModalVisible}
      onDownloadExample={onDownloadExample}
      importRecords={importRecords}
      control={control}
      isArchiveModalVisible={isArchiveModalVisible}
      isArchiveModalInfoVisible={isArchiveModalInfoVisible}
      latestArchivation={latestArchivation}
      isLineMark={isLineMark}
      onSortChange={onSortChange}
      onSearchChange={onSearchChange}
      onSelectAll={onSelectAll}
      onArchiveConfirm={onArchiveConfirm}
      onArchive={onArchive}
      onArchiveModalInfoOpen={onArchiveModalInfoOpen}
      onMoveToArchive={onMoveToArchive}
      onDeArchive={onDeArchive}
      onPageChange={onPageChange}
      isArchiveSelectedItemsVisible={isArchiveSelectedItemsVisible}
      setIsArchiveModalVisible={setIsArchiveModalVisible}
      setIsArchiveModalInfoVisible={setIsArchiveModalInfoVisible}
      isActionsVisible={isActionsVisible}
      items={items}
      selectedItems={selectedItems}
      currentTotalItems={currentTotalItems}
      onEditRecord={onEditRecord}
      onAddRecord={onAddRecord}
      isProfitCenterEmpty={isProfitCenterEmpty}
      meta={meta}
      naceCodeTreeData={naceCodeTreeData}
      isWithSearch={isWithSearch}
      isLoading={isLoading}
      isLazyCustomerExampleLoading={isLazyCustomerExampleLoading}
      onShowArchiveChange={onShowArchiveChange}
      activeModal={activeRestrictedModal}
      closeModal={onOpenRestrictedModal}
      archiveError={archiveError as IArchiveError || deArchiveError as IArchiveError || deleteError as IArchiveError}
      locationsCountries={locationsCountries?.data || []}
      onDeleteRecord={onDeleteRecord}
      isDeleteModalVisible={isDeleteModalVisible}
      onCloseDeleteModal={onCloseDeleteModal}
      onDeleteConfirm={onDeleteConfirm}
      isImportErrorModalVisible={isImportErrorModalVisible}
      importErrorMessage={importErrorMessage}
      onCloseImportErrorModal={handleCloseImportErrorModal}
      onImportError={handleImportError}
    />
  );
}

export default Customers;
